import PspSelect from "@/components/pspSelect/pspSelect";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { translate, TranslatorKeys } from "@/config/translator";
import { Switch } from "@fluentui/react-components";
import { useUploadNewMobile } from "./uploadNewMobile/useUploadNewMobile";
import { PspTextArea } from "@/components/pspTextArea/pspTextArea";
import PspButton from "@/components/pspButton/PspButton";
import { FileUploadArea } from "@/components/fileDropArea/fileUploadArea";
import { PspRadioGroup } from "@/components/pspRadioGroup/pspRadioGroup";
import { PspAvatar } from "@/components/pspAvatar/pspAvatar";
import { SelectedFilesList } from "@/components/selectedFilesList/selectedFilesList";
import Skeleton from "react-loading-skeleton";
import { beautifyBytes } from "@/config/utils";

export default function UploadNewMobile() {
  const {
    onBack,
    isTablet,
    documentType,
    noteState,
    setSelectedFiles,
    onChangeNote,
    onChangeWantsNote,
    onFilesChange,
    onUpload,
    selectedFiles,
    setDocumentType,
    lang,
    selectedMandate,
    payrollMonth,
    setPayrollMonth,
    months,
    extraYear,
    uploadButtonDisabled,
    isDataInvalid,
    documentTypeOptions,
    willBeSentToName,
    fileTooBig,
    uploadLimits,
  } = useUploadNewMobile();

  if (isTablet) return <></>;
  return (
    <>
      <TopBarMobile onBack={onBack} header="uploadFile" />
      <div className="p-6 flex flex-col h-fit">
        <div className="flex flex-col mb-2">
          <div className="text-font-secondary text-base">
            {translate("uploadFor", lang)}
          </div>
          <div className="font-medium text-base">
            {selectedMandate?.name ?? <Skeleton />}
          </div>
        </div>
        {documentTypeOptions && (
          <PspRadioGroup
            label={translate("selectDocumentType", lang)}
            options={documentTypeOptions}
            value={documentType}
            onChange={setDocumentType}
          />
        )}

        {willBeSentToName && (
          <>
            <div className="flex flex-row gap-2 items-center pt-2">
              <PspAvatar name={willBeSentToName} size="small" />
              <div className="text-base">
                <span className="text-font-secondary">
                  {translate("willBeSentTo", lang)}{" "}
                </span>
                <span className="text-font-black font-medium">
                  {willBeSentToName}
                </span>
              </div>
            </div>

            <div className="my-6">
              <div className="text-font-secondary mb-1">
                {selectedFiles && selectedFiles.length !== 0
                  ? translate("uploadedFiles", lang) +
                    ` (${selectedFiles.length})`
                  : translate("uploadFiles", lang)}
              </div>
              {selectedFiles && selectedFiles.length !== 0 && (
                <div className="mb-4">
                  <SelectedFilesList
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    uploadLimits={uploadLimits}
                  />
                  {fileTooBig === true && (
                    <span className="text-red-800">
                      {translate("allFilesTooBig", lang, {
                        "[bytes]": beautifyBytes(
                          uploadLimits?.maxRequestSizeBytes ?? 0,
                        ),
                      })}
                    </span>
                  )}
                </div>
              )}
              <FileUploadArea onChange={onFilesChange} />
            </div>
          </>
        )}

        {documentType === "payroll_accounting" && (
          <PspSelect
            onChange={setPayrollMonth}
            label="month"
            value={payrollMonth}
          >
            <>
              <option value="" disabled>
                {translate("selectOption", lang)}
              </option>
              {/* Normal rolling months */}
              {months.map(({ month, year }) => (
                <option key={`m-${month}-${year}`} value={month}>
                  {translate(`month${month}` as TranslatorKeys, lang)}, {year}
                </option>
              ))}

              {/* Special accounting months 13–15 */}
              {selectedMandate &&
                Array.from({
                  length: (selectedMandate.anzahlAbrechnungen ?? 15) - 12,
                }).map((_, i) => {
                  const value = 12 + i; // 12, 13, 14
                  return (
                    <option key={`extra-${value}`} value={value}>
                      {translate(`month${value}` as TranslatorKeys, lang)},{" "}
                      {extraYear}
                    </option>
                  );
                })}
            </>
          </PspSelect>
        )}

        {selectedFiles && selectedFiles.length !== 0 && (
          <div className="py-3">
            {/* WANTS NOTE */}
            <Switch
              label={translate("addNote", lang)}
              checked={noteState.wantsNote}
              onChange={(_, data) => onChangeWantsNote(data.checked)}
            />

            {/* NOTE */}
            {noteState.wantsNote && (
              <div className="flex flex-col gap-2">
                <PspTextArea
                  value={noteState.note}
                  label={"note"}
                  onChange={onChangeNote}
                />
                <p className="text-xs text-font-secondary italic">
                  {translate("noteWarning", lang)}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="grow" />
        {selectedFiles && selectedFiles.length !== 0 && (
          <PspButton
            appearance="primary"
            onClick={onUpload}
            disabled={uploadButtonDisabled || isDataInvalid}
          >
            {translate("upload", lang)}
          </PspButton>
        )}
      </div>
    </>
  );
}
